<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Cuentas por Cobrar
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Administración de cuentas pendientes de cobro
      </p>
    </div>

    <!-- Dashboard de Indicadores -->
    <div v-if="dashboard" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">Total por Cobrar</div>
        <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">${{ formatCurrency(dashboard.resumen.total_por_cobrar) }}</div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Vencido</div>
        <div class="text-2xl font-bold text-red-600 dark:text-red-400">${{ formatCurrency(dashboard.resumen.total_vencido) }}</div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">Próximas a Vencer</div>
        <div class="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{{ dashboard.resumen.proximas_vencer }}</div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">Pendientes</div>
        <div class="text-2xl font-bold text-gray-600 dark:text-gray-400">{{ dashboard.resumen.cuentas_pendientes }}</div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-1">Vencidas</div>
        <div class="text-2xl font-bold text-red-600 dark:text-red-400">{{ dashboard.resumen.cuentas_vencidas }}</div>
      </div>
    </div>

    <!-- Tabla Principal -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Cuentas
          </h2>
          <button @click="openModal()" class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nueva Cuenta por Cobrar
          </button>
        </div>

        <!-- Filtros -->
        <div class="flex flex-wrap gap-2">
          <input
            v-model="filtros.buscar"
            @keyup.enter="buscarCuentas"
            type="text"
            placeholder="Buscar por número de factura, concepto o cliente..."
            class="flex-1 min-w-[250px] px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <select
            v-model="filtros.estado"
            @change="buscarCuentas"
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Parcial</option>
            <option value="pagada">Pagada</option>
            <option value="vencida">Vencida</option>
          </select>
          <button @click="buscarCuentas" class="px-4 py-2 rounded bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition">
            Buscar
          </button>
          <button @click="limpiarFiltros" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">
            Limpiar
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-if="error" class="text-red-500 text-center mb-4">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">Factura</th>
                <th class="px-4 py-2 font-bold">Cliente</th>
                <th class="px-4 py-2 font-bold">Concepto</th>
                <th class="px-4 py-2 font-bold">Monto Total</th>
                <th class="px-4 py-2 font-bold">Saldo</th>
                <th class="px-4 py-2 font-bold">Vencimiento</th>
                <th class="px-4 py-2 font-bold">Estado</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cuenta in cuentas" :key="cuenta.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  {{ cuenta.numero_factura }}
                </td>
                <td class="px-4 py-2">
                  {{ cuenta.contacto?.nombre_completo }}
                </td>
                <td class="px-4 py-2 max-w-xs truncate">
                  {{ cuenta.concepto }}
                </td>
                <td class="px-4 py-2 font-semibold">
                  ${{ formatCurrency(cuenta.monto_total) }}
                </td>
                <td class="px-4 py-2 font-semibold" :class="cuenta.saldo_pendiente > 0 ? 'text-red-600' : 'text-green-600'">
                  ${{ formatCurrency(cuenta.saldo_pendiente) }}
                </td>
                <td class="px-4 py-2">
                  {{ formatDate(cuenta.fecha_vencimiento) }}
                  <span v-if="cuenta.dias_atraso > 0" class="block text-xs text-red-600">
                    {{ cuenta.dias_atraso }} días de atraso
                  </span>
                </td>
                <td class="px-4 py-2">
                  <span
                    :class="getEstadoClass(cuenta.estado)"
                    class="px-2 py-1 text-xs font-medium rounded-full"
                  >
                    {{ getEstadoLabel(cuenta.estado) }}
                  </span>
                </td>
                <td class="px-4 py-2">
                  <div class="flex gap-2">
                    <button @click="openPagoModal(cuenta)" v-if="cuenta.estado !== 'pagada'" class="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Registrar Pago">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </button>
                    <button @click="verDetalle(cuenta)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300" title="Ver Detalle">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button @click="openModal(cuenta)" class="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300" title="Editar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="eliminarCuenta(cuenta)" class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Eliminar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="cuentas.length === 0">
                <td colspan="8" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No se encontraron cuentas por cobrar
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div v-if="pagination.total > 0" class="flex justify-between items-center mt-6">
          <div class="text-sm text-gray-700 dark:text-gray-300">
            Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} de {{ pagination.total }} cuentas
          </div>
          <div class="flex gap-2">
            <button
              @click="cambiarPagina(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span class="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>
            <button
              @click="cambiarPagina(pagination.current_page + 1)"
              :disabled="pagination.current_page >= pagination.last_page"
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar Cuenta -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">
            {{ editingCuenta ? 'Editar Cuenta por Cobrar' : 'Nueva Cuenta por Cobrar' }}
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="submitForm">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Cliente -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cliente <span class="text-red-500">*</span>
              </label>
              <button
                type="button"
                @click="abrirSelectorContacto"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <span :class="contactoSeleccionado ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'">
                  {{ contactoSeleccionado ? contactoSeleccionado.nombre_completo : 'Seleccione un cliente' }}
                </span>
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </button>
              <input type="hidden" v-model="form.contacto_id" required />
            </div>

            <!-- Número de Factura -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Número de Factura <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.numero_factura"
                type="text"
                required
                maxlength="50"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <!-- Monto Total -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Monto Total <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.monto_total"
                type="number"
                step="0.01"
                min="0.01"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <!-- Fecha de Emisión -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Emisión <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.fecha_emision"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <!-- Fecha de Vencimiento -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Vencimiento <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.fecha_vencimiento"
                type="date"
                required
                :min="form.fecha_emision"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <!-- Concepto -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Concepto <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="form.concepto"
                required
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              ></textarea>
            </div>

            <!-- Notas -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notas
              </label>
              <textarea
                v-model="form.notas"
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              ></textarea>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
            >
              {{ loading ? 'Guardando...' : (editingCuenta ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Registrar Pago -->
    <div v-if="showPagoModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div class="flex justify-between items-center pb-3 border-b dark:border-gray-700">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">Registrar Pago</h3>
          <button @click="closePagoModal" type="button" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div v-if="cuentaParaPago" class="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Cliente</p>
              <p class="font-semibold text-gray-900 dark:text-white">{{ cuentaParaPago.contacto?.nombre_completo }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Factura</p>
              <p class="font-semibold text-gray-900 dark:text-white">{{ cuentaParaPago.numero_factura }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Saldo Pendiente</p>
              <p class="font-semibold text-red-600 dark:text-red-400">{{ formatCurrency(cuentaParaPago.saldo_pendiente) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">Monto Total</p>
              <p class="font-semibold text-gray-900 dark:text-white">{{ formatCurrency(cuentaParaPago.monto_total) }}</p>
            </div>
          </div>
        </div>

        <form @submit.prevent="submitPago" class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Monto a Pagar <span class="text-red-500">*</span>
              </label>
              <input
                v-model="formPago.monto"
                type="number"
                step="0.01"
                min="0.01"
                :max="cuentaParaPago?.saldo_pendiente"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Pago <span class="text-red-500">*</span>
              </label>
              <input
                v-model="formPago.fecha_pago"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Método de Pago <span class="text-red-500">*</span>
              </label>
              <select
                v-model="formPago.metodo_pago"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta_credito">Tarjeta de Crédito</option>
                <option value="tarjeta_debito">Tarjeta de Débito</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Referencia/Número de Transacción
              </label>
              <input
                v-model="formPago.referencia"
                type="text"
                maxlength="100"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notas
              </label>
              <textarea
                v-model="formPago.notas"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              ></textarea>
            </div>
          </div>

          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              @click="closePagoModal"
              class="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700"
            >
              Registrar Pago
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Detalle Mejorado -->
    <div v-if="showDetalleModal && cuentaDetalle" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-6xl shadow-lg rounded-md bg-white dark:bg-gray-800 mb-10">
        <div class="flex justify-between items-center pb-3 border-b dark:border-gray-700">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white">Factura #{{ cuentaDetalle.numero_factura }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">ID: {{ cuentaDetalle.id }}</p>
          </div>
          <button @click="closeDetalleModal" type="button" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <!-- Columna Izquierda - Información Principal -->
          <div class="lg:col-span-2 space-y-4">
            <!-- Resumen de Estado -->
            <div class="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-violet-200 dark:border-violet-800">
              <div class="flex items-center justify-between mb-4">
                <span
                  :class="getEstadoClass(cuentaDetalle.estado)"
                  class="px-3 py-1 text-sm font-semibold rounded-full"
                >
                  {{ getEstadoLabel(cuentaDetalle.estado) }}
                </span>
                <div class="text-right">
                  <p class="text-xs text-gray-600 dark:text-gray-400">Progreso de pago</p>
                  <p class="text-2xl font-bold text-violet-600 dark:text-violet-400">{{ cuentaDetalle.porcentaje_pagado }}%</p>
                </div>
              </div>
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  class="bg-gradient-to-r from-violet-500 to-purple-500 h-3 rounded-full transition-all duration-300"
                  :style="{ width: cuentaDetalle.porcentaje_pagado + '%' }"
                ></div>
              </div>
            </div>

            <!-- Montos -->
            <div class="grid grid-cols-3 gap-4">
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">Monto Total</p>
                <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ formatCurrency(cuentaDetalle.monto_total) }}</p>
              </div>
              <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <p class="text-xs text-green-700 dark:text-green-400 mb-1">Pagado</p>
                <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ formatCurrency(cuentaDetalle.monto_pagado) }}</p>
              </div>
              <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                <p class="text-xs text-red-700 dark:text-red-400 mb-1">Pendiente</p>
                <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ formatCurrency(cuentaDetalle.saldo_pendiente) }}</p>
              </div>
            </div>

            <!-- Información del Cliente y Fechas -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                Información del Cliente
              </h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-xs text-gray-600 dark:text-gray-400">Cliente</p>
                  <p class="font-semibold text-gray-900 dark:text-white">{{ cuentaDetalle.contacto?.nombre_completo }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-600 dark:text-gray-400">RFC</p>
                  <p class="font-semibold text-gray-900 dark:text-white">{{ cuentaDetalle.contacto?.rfc || '-' }}</p>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                Fechas Importantes
              </h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-xs text-gray-600 dark:text-gray-400">Fecha de Emisión</p>
                  <p class="font-semibold text-gray-900 dark:text-white">{{ formatDate(cuentaDetalle.fecha_emision) }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-600 dark:text-gray-400">Fecha de Vencimiento</p>
                  <p class="font-semibold" :class="cuentaDetalle.dias_atraso > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'">
                    {{ formatDate(cuentaDetalle.fecha_vencimiento) }}
                  </p>
                </div>
                <div class="col-span-2">
                  <p class="text-xs text-gray-600 dark:text-gray-400">Estado de Vencimiento</p>
                  <p class="font-semibold" :class="cuentaDetalle.dias_atraso > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                    <span v-if="cuentaDetalle.dias_atraso > 0">⚠️ Vencida hace {{ cuentaDetalle.dias_atraso }} días</span>
                    <span v-else>✅ Al corriente</span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Concepto -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Concepto</h4>
              <p class="text-sm text-gray-700 dark:text-gray-300">{{ cuentaDetalle.concepto }}</p>
            </div>

            <!-- Notas -->
            <div v-if="cuentaDetalle.notas" class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
              <h4 class="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-2 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                </svg>
                Notas
              </h4>
              <p class="text-sm text-yellow-800 dark:text-yellow-200">{{ cuentaDetalle.notas }}</p>
            </div>

            <!-- Historial de Pagos -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Historial de Pagos ({{ cuentaDetalle.pagos?.length || 0 }})
              </h4>
              <div v-if="cuentaDetalle.pagos && cuentaDetalle.pagos.length > 0" class="space-y-3">
                <div v-for="pago in cuentaDetalle.pagos" :key="pago.id"
                     class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div class="flex justify-between items-start mb-2">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="font-bold text-green-600 dark:text-green-400">{{ formatCurrency(pago.monto) }}</span>
                        <span class="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                          {{ getMetodoPagoLabel(pago.metodo_pago) }}
                        </span>
                      </div>
                      <p class="text-xs text-gray-600 dark:text-gray-400">
                        📅 {{ formatDate(pago.fecha_pago) }}
                        <span v-if="pago.referencia"> • 🔖 {{ pago.referencia }}</span>
                      </p>
                      <p v-if="pago.notas" class="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        {{ pago.notas }}
                      </p>
                      <p v-if="pago.created_by" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        👤 Registrado por: {{ pago.created_by.nombre }}
                      </p>
                    </div>
                    <button
                      @click="eliminarPago(cuentaDetalle.id, pago.id)"
                      class="ml-2 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      title="Eliminar pago"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-6 text-gray-500 dark:text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p>No hay pagos registrados</p>
              </div>
            </div>
          </div>

          <!-- Columna Derecha - Timeline y Auditoría -->
          <div class="space-y-4">
            <!-- Información de Auditoría -->
            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Información de Auditoría
              </h4>
              <div class="space-y-3">
                <div>
                  <p class="text-xs text-blue-700 dark:text-blue-400 mb-1">Creado por</p>
                  <p class="text-sm font-semibold text-blue-900 dark:text-blue-200">
                    {{ cuentaDetalle.created_by?.nombre || 'Sistema' }}
                  </p>
                  <p class="text-xs text-blue-600 dark:text-blue-400">
                    {{ formatDateTime(cuentaDetalle.created_at) }}
                  </p>
                </div>
                <div v-if="cuentaDetalle.updated_by">
                  <p class="text-xs text-blue-700 dark:text-blue-400 mb-1">Última modificación</p>
                  <p class="text-sm font-semibold text-blue-900 dark:text-blue-200">
                    {{ cuentaDetalle.updated_by?.nombre || 'Sistema' }}
                  </p>
                  <p class="text-xs text-blue-600 dark:text-blue-400">
                    {{ formatDateTime(cuentaDetalle.updated_at) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Timeline de Eventos -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Timeline de Eventos
              </h4>
              <div class="space-y-3">
                <!-- Evento: Creación -->
                <div class="flex gap-2">
                  <div class="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-green-500"></div>
                  <div class="flex-1">
                    <p class="text-xs font-semibold text-gray-900 dark:text-white">Factura creada</p>
                    <p class="text-xs text-gray-600 dark:text-gray-400">{{ formatDateTime(cuentaDetalle.created_at) }}</p>
                  </div>
                </div>

                <!-- Eventos: Pagos -->
                <div v-for="pago in cuentaDetalle.pagos" :key="'timeline-' + pago.id" class="flex gap-2">
                  <div class="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500"></div>
                  <div class="flex-1">
                    <p class="text-xs font-semibold text-gray-900 dark:text-white">
                      Pago registrado: {{ formatCurrency(pago.monto) }}
                    </p>
                    <p class="text-xs text-gray-600 dark:text-gray-400">{{ formatDateTime(pago.created_at) }}</p>
                  </div>
                </div>

                <!-- Estado actual -->
                <div class="flex gap-2">
                  <div class="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full" :class="cuentaDetalle.estado === 'pagada' ? 'bg-green-500' : 'bg-yellow-500'"></div>
                  <div class="flex-1">
                    <p class="text-xs font-semibold text-gray-900 dark:text-white">Estado actual</p>
                    <p class="text-xs" :class="getEstadoClass(cuentaDetalle.estado)">
                      {{ getEstadoLabel(cuentaDetalle.estado) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estadísticas Rápidas -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Estadísticas</h4>
              <div class="space-y-2">
                <div class="flex justify-between text-xs">
                  <span class="text-gray-600 dark:text-gray-400">Total de pagos</span>
                  <span class="font-semibold text-gray-900 dark:text-white">{{ cuentaDetalle.pagos?.length || 0 }}</span>
                </div>
                <div class="flex justify-between text-xs">
                  <span class="text-gray-600 dark:text-gray-400">Promedio por pago</span>
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {{ cuentaDetalle.pagos?.length ? formatCurrency(cuentaDetalle.monto_pagado / cuentaDetalle.pagos.length) : '$0.00' }}
                  </span>
                </div>
                <div class="flex justify-between text-xs">
                  <span class="text-gray-600 dark:text-gray-400">Días desde emisión</span>
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {{ Math.abs(cuentaDetalle.dias_vencimiento) + cuentaDetalle.dias_atraso }} días
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3 border-t dark:border-gray-700 pt-4">
          <button
            @click="closeDetalleModal"
            class="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Selector de Contacto -->
    <SelectorContacto
      v-if="showSelectorContacto"
      @close="cerrarSelectorContacto"
      @select="seleccionarContacto"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useHttp } from '@/composables/useHttp.js';
import Swal from 'sweetalert2';
import SelectorContacto from '@/components/shared/SelectorContacto.vue';

const { get, post, put, delete: del, data } = useHttp();

const cuentas = ref([]);
const contactos = ref([]);
const dashboard = ref(null);
const loading = ref(false);
const error = ref('');
const showModal = ref(false);
const showPagoModal = ref(false);
const showDetalleModal = ref(false);
const showSelectorContacto = ref(false);
const editingCuenta = ref(null);
const cuentaParaPago = ref(null);
const cuentaDetalle = ref(null);
const contactoSeleccionado = ref(null);

const filtros = ref({
  buscar: '',
  estado: ''
});

const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0
});

const form = ref({
  contacto_id: '',
  numero_factura: '',
  concepto: '',
  monto_total: '',
  fecha_emision: '',
  fecha_vencimiento: '',
  notas: ''
});

const formPago = ref({
  monto: '',
  fecha_pago: new Date().toISOString().split('T')[0],
  metodo_pago: 'efectivo',
  referencia: '',
  notas: ''
});

const resetForm = () => {
  form.value = {
    contacto_id: '',
    numero_factura: '',
    concepto: '',
    monto_total: '',
    fecha_emision: '',
    fecha_vencimiento: '',
    notas: ''
  };
};

const resetFormPago = () => {
  formPago.value = {
    monto: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    metodo_pago: 'efectivo',
    referencia: '',
    notas: ''
  };
};

const abrirSelectorContacto = () => {
  showSelectorContacto.value = true;
};

const cerrarSelectorContacto = () => {
  showSelectorContacto.value = false;
};

const seleccionarContacto = (contacto) => {
  contactoSeleccionado.value = contacto;
  form.value.contacto_id = contacto.id;
};

const openModal = (cuenta = null) => {
  editingCuenta.value = cuenta;
  if (cuenta) {
    form.value = {
      contacto_id: cuenta.contacto_id,
      numero_factura: cuenta.numero_factura,
      concepto: cuenta.concepto,
      monto_total: cuenta.monto_total,
      fecha_emision: cuenta.fecha_emision,
      fecha_vencimiento: cuenta.fecha_vencimiento,
      notas: cuenta.notas || ''
    };
    // Establecer el contacto seleccionado si estamos editando
    contactoSeleccionado.value = cuenta.contacto;
  } else {
    resetForm();
    contactoSeleccionado.value = null;
  }
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingCuenta.value = null;
  contactoSeleccionado.value = null;
  resetForm();
};

const openPagoModal = (cuenta) => {
  cuentaParaPago.value = cuenta;
  resetFormPago();
  formPago.value.monto = cuenta.saldo_pendiente;
  showPagoModal.value = true;
};

const closePagoModal = () => {
  showPagoModal.value = false;
  cuentaParaPago.value = null;
  resetFormPago();
};

const verDetalle = async (cuenta) => {
  try {
    await get(`/cuentas-por-cobrar/${cuenta.id}`);
    cuentaDetalle.value = data.value;
    showDetalleModal.value = true;
  } catch (err) {
    Swal.fire('Error', 'No se pudo cargar el detalle de la cuenta', 'error');
  }
};

const closeDetalleModal = () => {
  showDetalleModal.value = false;
  cuentaDetalle.value = null;
};

const cargarDashboard = async () => {
  try {
    await get('/cuentas-por-cobrar/dashboard');
    dashboard.value = data.value;
  } catch (err) {
    console.error('Error al cargar dashboard:', err);
  }
};

const cargarContactos = async () => {
  try {
    await get('/contactos?per_page=1000&estatus=activo');
    contactos.value = data.value.data;
  } catch (err) {
    console.error('Error al cargar contactos:', err);
  }
};

const cargarCuentas = async (pagina = 1) => {
  try {
    loading.value = true;
    error.value = '';

    const params = new URLSearchParams({
      page: pagina,
      per_page: pagination.value.per_page
    });

    if (filtros.value.buscar) {
      params.append('buscar', filtros.value.buscar);
    }
    if (filtros.value.estado) {
      params.append('estado', filtros.value.estado);
    }

    await get(`/cuentas-por-cobrar?${params}`);
    cuentas.value = data.value.data;
    pagination.value = {
      current_page: data.value.current_page,
      last_page: data.value.last_page,
      per_page: data.value.per_page,
      total: data.value.total
    };
  } catch (err) {
    error.value = 'Error al cargar cuentas por cobrar';
  } finally {
    loading.value = false;
  }
};

const buscarCuentas = () => {
  cargarCuentas(1);
};

const limpiarFiltros = () => {
  filtros.value = {
    buscar: '',
    estado: ''
  };
  cargarCuentas(1);
};

const cambiarPagina = (pagina) => {
  if (pagina >= 1 && pagina <= pagination.value.last_page) {
    cargarCuentas(pagina);
  }
};

const submitForm = async () => {
  try {
    loading.value = true;

    if (editingCuenta.value) {
      await put(`/cuentas-por-cobrar/${editingCuenta.value.id}`, form.value);
      Swal.fire('¡Éxito!', 'Cuenta actualizada correctamente', 'success');
    } else {
      await post('/cuentas-por-cobrar', form.value);
      Swal.fire('¡Éxito!', 'Cuenta creada correctamente', 'success');
    }

    closeModal();
    cargarCuentas(pagination.value.current_page);
    cargarDashboard();
  } catch (err) {
    const message = err.response?.data?.message || 'Error al procesar la solicitud';
    Swal.fire('Error', message, 'error');
  } finally {
    loading.value = false;
  }
};

const submitPago = async () => {
  try {
    loading.value = true;

    await post(`/cuentas-por-cobrar/${cuentaParaPago.value.id}/pagos`, formPago.value);
    Swal.fire('¡Éxito!', 'Pago registrado correctamente', 'success');

    closePagoModal();
    cargarCuentas(pagination.value.current_page);
    cargarDashboard();
  } catch (err) {
    const message = err.response?.data?.message || 'Error al registrar el pago';
    Swal.fire('Error', message, 'error');
  } finally {
    loading.value = false;
  }
};

const eliminarCuenta = async (cuenta) => {
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar la cuenta ${cuenta.numero_factura}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      await del(`/cuentas-por-cobrar/${cuenta.id}`);
      Swal.fire('¡Eliminado!', 'Cuenta eliminada correctamente', 'success');
      cargarCuentas(pagination.value.current_page);
      cargarDashboard();
    }
  } catch (err) {
    const message = err.response?.data?.message || 'Error al eliminar cuenta';
    Swal.fire('Error', message, 'error');
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (datetime) => {
  if (!datetime) return '';
  const d = new Date(datetime);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getEstadoClass = (estado) => {
  const classes = {
    pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    parcial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    pagada: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    vencida: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
  };
  return classes[estado] || '';
};

const getEstadoLabel = (estado) => {
  const labels = {
    pendiente: 'Pendiente',
    parcial: 'Parcial',
    pagada: 'Pagada',
    vencida: 'Vencida'
  };
  return labels[estado] || estado;
};

const getMetodoPagoLabel = (metodo) => {
  const labels = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    cheque: 'Cheque',
    tarjeta_credito: 'Tarjeta de Crédito',
    tarjeta_debito: 'Tarjeta de Débito',
    otro: 'Otro'
  };
  return labels[metodo] || metodo;
};

onMounted(() => {
  cargarDashboard();
  cargarContactos();
  cargarCuentas();
});
</script>
